// Copyright (C) 2021 Bystronic Laser AG

import i18n from '@/i18n';
import { currencyCode } from '@/utils/currency';
import { currentLocale } from '@/utils/i18n';
import { isNil, Nilable } from '@/utils/misc';

export function decimalFormatter(value: any, maximumFractionDigits = 2) {
  return i18n.n(value, { maximumFractionDigits });
}

/**
 * Returns a compact representation of value by reducing the digits with an
 * abbreviation.
 *
 * Example: 1200 -> 1.2K
 *
 * @param value Number to format in abbreviated form.
 * @param maximumFractionDigits The maximum number of decimal places to return.
 *  If undefined, returns a variable number of decimal places.
 */
export function abbreviateNumber(
  value: number,
  maximumFractionDigits?: number,
): string {
  return new Intl.NumberFormat(currentLocale(), {
    notation: 'compact',
    maximumFractionDigits,
  }).format(value);
}

export function abbreviateNumberWithCurrency(n: number): string {
  return new Intl.NumberFormat(currentLocale(), {
    notation: 'compact',
    style: 'currency',
    currency: currencyCode(),
  }).format(n);
}

export function numberWithCurrency(n: number): string {
  return new Intl.NumberFormat(currentLocale(), {
    style: 'currency',
    currency: currencyCode(),
  }).format(n);
}

export function formatPercentage(
  percentage: number,
  maximumFractionDigits: number = 2,
): string {
  const adjustedPercentage =
    percentage < 0 && percentage > -1 // avoid -0 %
      ? 0
      : percentage;

  return new Intl.NumberFormat(currentLocale(), {
    style: 'percent',
    maximumFractionDigits,
    // percent style expects a fraction of one, so we have to divide by 100
  }).format(adjustedPercentage / 100);
}

export function formatPercentageOrDefault(
  value: number | null,
  defaultValue = '-',
  maximumFractionDigits: number = 2,
): string {
  if (isNil(value)) {
    return defaultValue;
  }
  return formatPercentage(value, maximumFractionDigits);
}

export function abbreviateNumberWithUnit(
  value: number,
  unit: string | null | undefined,
  maximumFractionDigits?: number,
) {
  return `${abbreviateNumber(value, maximumFractionDigits)}${
    unit ? ' ' + unit : ''
  }`;
}

/**
 * Returns the unit translation as singular or plural depending on value.
 *
 * Returns an empty string when unit is null.
 *
 * @param unit Key to be used to get the translation or null.
 * @param value Number to which unit applies to chose between singular and plural.
 */
export function translateUnit(
  unit: string | null,
  value: number | null,
): string {
  return !isNil(unit) ? i18n.tc(unit, value ?? 0).toString() : '';
}

export function formatWithUnit(
  value: number,
  unitKey: Nilable<string>,
): string {
  return (
    decimalFormatter(value) +
    (unitKey ? ' ' + translateUnit(unitKey, value) : '')
  );
}
